import { logger } from '../../utils/logger';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextZustand';
import { hasAllEffectivePermissions } from '../../constants/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  /** Todas as keys devem estar em `user.permissions` (AND). Evite duplicar o que `PermissionBasedRoute` já exige por rota. */
  requiredPermissions?: string[];
  allowSubscriptionUsers?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredPermissions = [],
  allowSubscriptionUsers = false,
}) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Permite acesso à página de convite mesmo estando logado
  const publicPagesAllowedWhenAuthenticated = ['/invite', '/pricing'];
  const isPublicPageAllowed = publicPagesAllowedWhenAuthenticated.some((path) =>
    location.pathname.startsWith(path)
  );

  if (!requireAuth && isAuthenticated && user && !isPublicPageAllowed) {
    if (user.needsSubscription) {
      return <Navigate to="/subscription" replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  const isRestrictedBySubscription =
    user?.needsSubscription === true ||
    user?.accessLevel === 'RESTRICTED' ||
    user?.accessLevel === 'BLOCKED';

  if (requireAuth && isAuthenticated && isRestrictedBySubscription) {
    const allowedPaths = ['/subscription', '/settings', '/notifications'];
    const isAllowedPath = allowedPaths.includes(location.pathname);

    logger.log('🔒 [ProtectedRoute] Verificando acesso needsSubscription:', {
      pathname: location.pathname,
      isAllowedPath,
      allowSubscriptionUsers,
    });

    if (!isAllowedPath && !allowSubscriptionUsers) {
      logger.log('🔒 [ProtectedRoute] Acesso restrito será tratado pela rota interna');
    }

    logger.log('✅ [ProtectedRoute] Acesso permitido (subscription/settings)');
  }

  if (
    requireAuth &&
    isAuthenticated &&
    user &&
    requiredPermissions.length > 0 &&
    !hasAllEffectivePermissions(user.permissions, requiredPermissions)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
